import { useEffect, useRef } from "react";

/** Interactive hadron collider core — orbiting particles on tilted rings, cursor-reactive. */
export const ParticleCore = () => {
  const canvasRef = useRef(null);
  const mouse = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let raf, w, h, dpr = Math.min(window.devicePixelRatio || 1, 2);
    let t = 0;

    const N = 170;
    const parts = [];
    for (let i = 0; i < N; i++) {
      const ring = i % 3;
      parts.push({
        ring,
        a: Math.random() * Math.PI * 2,
        r: 0.5 + ring * 0.17 + Math.random() * 0.06,
        speed: (0.22 + ring * 0.1 + Math.random() * 0.05) * (ring === 1 ? -1 : 1),
        size: 0.8 + Math.random() * 1.6,
        cyan: Math.random() > 0.35,
      });
    }
    const pulses = [];
    let lastPulse = 0;

    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      w = rect.width; h = rect.height;
      canvas.width = w * dpr; canvas.height = h * dpr;
      canvas.style.width = w + "px"; canvas.style.height = h + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const cl = (v) => Math.max(-1, Math.min(1, v));
      mouse.current.x = cl((e.clientX - rect.left) / Math.max(rect.width, 1) - 0.5) * 2;
      mouse.current.y = cl((e.clientY - rect.top) / Math.max(rect.height, 1) - 0.5) * 2;
    };
    window.addEventListener("mousemove", onMove);

    const loop = (ts) => {
      t += 0.016;
      ctx.clearRect(0, 0, w, h);
      const cx = w / 2, cy = h / 2;
      const R = Math.min(w, h) * 0.46;
      const tilt = 0.34 + mouse.current.y * 0.08;
      const rot = -0.42 + mouse.current.x * 0.12;

      // orbit guide rings
      for (let g = 0; g < 3; g++) {
        ctx.beginPath();
        ctx.ellipse(cx, cy, R * (0.5 + g * 0.17), R * (0.5 + g * 0.17) * tilt, rot, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(168,214,127,${0.10 + g * 0.03})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      // nucleus
      const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, R * 0.3);
      grad.addColorStop(0, "rgba(168,214,127,0.5)");
      grad.addColorStop(0.4, "rgba(23,128,60,0.26)");
      grad.addColorStop(1, "rgba(6,19,12,0)");
      ctx.fillStyle = grad;
      ctx.beginPath(); ctx.arc(cx, cy, R * 0.3, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(cx, cy, R * 0.07, 0, Math.PI * 2);
      ctx.fillStyle = "#F0F7EA"; ctx.fill();

      // particles
      for (const p of parts) {
        const a = p.a + t * p.speed;
        const ex = Math.cos(a) * R * p.r;
        const ey = Math.sin(a) * R * p.r * tilt;
        const x = cx + ex * Math.cos(rot) - ey * Math.sin(rot);
        const y = cy + ex * Math.sin(rot) + ey * Math.cos(rot);
        const depth = (Math.sin(a) + 1) / 2; // 0 back — 1 front
        ctx.beginPath();
        ctx.arc(x, y, p.size * (0.6 + depth * 0.9), 0, Math.PI * 2);
        ctx.fillStyle = p.cyan
          ? `rgba(168,214,127,${0.25 + depth * 0.7})`
          : `rgba(240,247,234,${0.15 + depth * 0.55})`;
        ctx.fill();
      }

      // periodic collision pulse
      if (ts - lastPulse > 4200) { pulses.push({ r: R * 0.07, alpha: 0.6 }); lastPulse = ts; }
      for (let i = pulses.length - 1; i >= 0; i--) {
        const pu = pulses[i];
        pu.r += 2.4; pu.alpha -= 0.008;
        if (pu.alpha <= 0) { pulses.splice(i, 1); continue; }
        ctx.beginPath(); ctx.ellipse(cx, cy, pu.r, pu.r * tilt, rot, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(16,185,129,${pu.alpha})`;
        ctx.lineWidth = 1.4; ctx.stroke();
      }

      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMove);
    };
  }, []);

  return <canvas ref={canvasRef} data-testid="hero-particle-canvas" aria-hidden="true" className="absolute inset-0" />;
};
